// Replays the offline queue on app start and whenever the browser comes back online; sent items
// refresh the query cache and get one toast, refused items surface as persistent notices in the
// banner (they are stored by the queue itself). An expired session stops the replay and drops
// the `me` cache so the router sends the employee to the login page; the items wait for them.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { queryKeys } from "@/api/queries/keys";
import { useToast } from "@/components/ui/Toast";
import { offlineQueue } from "./offline-queue";

export function useOfflineReplay(userId: string): () => Promise<void> {
  const { t } = useTranslation("translation", { keyPrefix: "pwa" });
  const client = useQueryClient();
  const toast = useToast();

  const replay = useCallback(async () => {
    try {
      offlineQueue.setUser(userId);
      await offlineQueue.init();
      if (offlineQueue.items().length === 0) return;
      const result = await offlineQueue.replay();
      if (result.sent > 0) {
        await client.invalidateQueries();
        toast({ tone: "success", title: t("offline.sentToast", { count: result.sent }) });
      }
      if (result.stopped === "unauthorized") {
        await client.invalidateQueries({ queryKey: queryKeys.me });
      }
    } catch (error) {
      // Never let a replay error surface as an unhandled rejection; the banner keeps the count.
      console.error("offline replay failed", error);
    }
  }, [client, toast, t, userId]);

  useEffect(() => {
    void replay();
    const onOnline = () => void replay();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [replay]);

  return replay;
}
